import { z } from 'zod';
import { numericString } from '../data/schemas/backend.schema';

/** Toneladas a kg, redondeado a kg enteros para evitar errores de coma flotante. */
const tonsToKg = (t: number) => Math.round(t * 1000);

export const AnalisisSchema = z.object({
  itemCode: z.number(),
  description: z.string(),
  result: z.number(),
  analysisSequence: z.number(),
  date: z.string(),
});

export const LoteAsignacionSchema = z.object({
  companyCode: z.number(),
  plantCode: z.number(),
  productCode: z.number(),
  batchCode: z.string(),
  harvestYear: z.number(),
  groupLot: z.string(),
  variety: z.string(),
  caliber: z.string(),
  germinationPower: z.number(),
  stock: z.number(),
  bagCount: z.number(),
  committed: z.number(),
  destinationName: z.string(),
  assignmentDate: z.string(),
  areaCode: z.number(),
  areaDescription: z.string(),
  analysis: z.array(AnalisisSchema),
});

/** Destino tal como lo devuelve `GET /lot-assignments/destinations`; `id` es el `un01NroOpe` del commit. */
export const DestinoSchema = z
  .object({
    name: z.string(),
    operationNumber: z.number(),
    customer: z.string(),
    active: z.boolean(),
  })
  .transform((d) => ({ id: d.operationNumber, nombre: d.name, cliente: d.customer, activa: d.active }));

/** Bolsón de un lote tal como lo devuelve `GET /lot-assignments/lots/bags` (claves del WS sin mapear); `Stock` y `Comprometido_Un01` llegan como string. */
export const BolsonLoteSchema = z
  .object({
    PLote_SecPro: z.number(),
    Stock: numericString,
    Area: z.union([z.string(), z.number()]),
    Fila: z.number(),
    Columna: z.number(),
    Comprometido_Un01: numericString.optional(),
  })
  .transform((b) => ({
    processSequence: b.PLote_SecPro,
    /** Stock en kg; el WS lo informa en toneladas. */
    stock: tonsToKg(b.Stock),
    area: String(b.Area),
    rowNumber: b.Fila,
    columnNumber: b.Columna,
    /** Operación a la que está comprometido el bolsón; 0 si está libre. */
    committedTo: b.Comprometido_Un01 ?? 0,
  }));

/** El backend envía `{ items, Message }`; sin bolsones puede venir solo `Message`. */
export const BolsonesRespSchema = z
  .object({ data: z.object({ items: z.array(BolsonLoteSchema).optional() }) })
  .transform((r) => r.data.items ?? []);

export const LotesRespSchema = z.object({
  data: z.array(LoteAsignacionSchema),
});

export const DestinosRespSchema = z.object({
  data: z.array(DestinoSchema),
});

export const NuevaAsignacionSchema = z.object({
  batchCode: z.string(),
  productCode: z.number(),
  harvestYear: z.number(),
  groupLot: z.string(),
  un01NroOpe: z.number(),
  areaCode: z.number(),
  assignmentDate: z.string(),
  secPros: z.array(z.number()),
});

/** Envelope global del backend para `commit` y `release`; los errores de GNexus llegan como estado HTTP. */
export const AccionRespSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  data: z.unknown().optional(),
});

export type Analisis = z.infer<typeof AnalisisSchema>;
export type LoteAsignacion = z.infer<typeof LoteAsignacionSchema>;
export type Destino = z.output<typeof DestinoSchema>;
export type BolsonLote = z.output<typeof BolsonLoteSchema>;

/** Filtros opcionales de `GET /lot-assignments/lots`. */
export interface LotesFiltro {
  harvestYear?: number;
  batchCode?: string;
  productCode?: number;
  onlyFree?: boolean;
}

/** Lote cuyos bolsones se consultan. */
export type BolsonesFiltro = Pick<LoteAsignacion, 'productCode' | 'batchCode' | 'harvestYear' | 'groupLot'>;
export type NuevaAsignacion = z.infer<typeof NuevaAsignacionSchema>;
export type NuevaAsignacionResp = z.infer<typeof AccionRespSchema>;

/** Bolsones comprometidos a una operación que se liberan. */
export type LiberacionBolsones = Omit<NuevaAsignacion, 'areaCode' | 'assignmentDate'>;

const normalizeAnalisis = (s: string): string =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export const poderGerminativo = (lote: LoteAsignacion): number =>
  lote.analysis.find((a) => /poder(?:\s+de)?\s*germ/i.test(normalizeAnalisis(a.description)))?.result ?? lote.germinationPower;

export const energiaGerminacion = (lote: LoteAsignacion): number =>
  lote.analysis.find((a) => /energia(?:\s+de)?\s+germinacion/i.test(normalizeAnalisis(a.description)))?.result ?? 0;
